/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './MailingAddress.scss';

import { array, func, string, object } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import ProfileLabeledInput from 'components/layouts/Preferences/ProfileLabeledInput';

import {
  isValidAddressOne,
  isValidAddressTwo,
  isValidCity,
  isValidCountry,
  isValidPostalCode,
  handleMultipleValidations,
  isFieldRequired,
  isResidentialPOBox,
  isPOBox
} from 'services/Validation';
import { getCountries } from 'state/api/MetadataSelectors';
import Store from 'state/Store';
import { touchedField } from 'state/validation/ValidationActions';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import TextInput from 'components/shared/Text/TextInput';
import SelectDropdown from './SelectDropdown';
import { getUpdatedFieldName, shouldFieldValidate } from 'support/AddressUtils';
import { hasResidentialAddress } from 'state/customer/CustomerSelectors';
import { mailingTypes } from 'services/Preferences';

const AddressOneWithPermissions = WithUserPermissions(TextInput);
const AddressTwoWithPermissions = WithUserPermissions(TextInput);
const AddressCityWithPermissions = WithUserPermissions(TextInput);
const AddressCountryWithPermissions = WithUserPermissions(SelectDropdown);
const AddressZipCodeWithPermissions = WithUserPermissions(TextInput);

const NonUSAddress = ({
  address,
  countries,
  onChange,
  invalidFields,
  mailingType,
  hasResidential,
  touchedFields
}) => {
  const onFieldChange = (field) => (event) => {
    const value = event.target.value;
    onChange(field, value);
  };

  const getFieldName = field => getUpdatedFieldName(field, hasResidential, mailingType)
  const shouldValidateField = field => shouldFieldValidate(field, touchedFields)

  const onFieldBlur = (field) => () =>
    Store.dispatch(touchedField(getFieldName(field)));

    const addressOneValidations =
    hasResidential && mailingType === mailingTypes.residential
      ? [isFieldRequired, isValidAddressOne, isResidentialPOBox]
      : hasResidential && mailingType === mailingTypes.mailing
      ? [isFieldRequired, isValidAddressOne]
      : [isFieldRequired, isValidAddressOne, isPOBox];

  const addressTwoValidations =
    hasResidential && mailingType === mailingTypes.residential
      ? [isValidAddressTwo, isResidentialPOBox]
      : hasResidential && mailingType === mailingTypes.mailing
      ? [isValidAddressTwo]
      : [isValidAddressTwo, isPOBox];

  const addressValidator = (field) => ({
    field,
    shouldValidate: shouldValidateField(getFieldName(field)),
    validator:
      field === 'nonUSAddressOne'
        ? (value) =>
            handleMultipleValidations(value, addressOneValidations)
        : (value) =>
            handleMultipleValidations(value, addressTwoValidations),
    value: address[field]
  });

  const cityValidator = {
    field: 'nonUSCity',
    shouldValidate: shouldValidateField(getFieldName("nonUSCity")),
    validator: (value) =>
      handleMultipleValidations(value, [isFieldRequired, isValidCity]),
    value: address.nonUSCity
  };

  const postCodeValidator = {
    field: 'postalCode',
    validator: (value) =>
      handleMultipleValidations(value, [isFieldRequired, isValidPostalCode]),
    value: address.postalCode,
    shouldValidate: shouldValidateField(getFieldName("postalCode"))
  };

  const countryValidator = {
    field: 'country',
    validator: (value) =>
      handleMultipleValidations(value, [isFieldRequired, isValidCountry]),
    value: address.country,
    shouldValidate: shouldValidateField(getFieldName("country"))
  };

  return (
    <Fragment>
      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('nonUSAddressOne')]}
        label="Address 1"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[addressValidator('nonUSAddressOne')]}>
        <AddressOneWithPermissions
          onBlur={onFieldBlur('nonUSAddressOne')}
          onChange={onFieldChange('nonUSAddressOne')}
          value={address.nonUSAddressOne}
          className="address-input"
          dataTestId={getFieldName("nonUSAddressOne")}
          shouldMask
        />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('nonUSAddressTwo')]}
        label="Address 2"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[addressValidator('nonUSAddressTwo')]}>
        <AddressTwoWithPermissions
          onBlur={onFieldBlur('nonUSAddressTwo')}
          onChange={onFieldChange('nonUSAddressTwo')}
          value={address.nonUSAddressTwo}
          dataTestId={getFieldName("nonUSAddressTwo")}
          className="address-input"
          shouldMask
        />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('nonUSCity')]}
        label="City"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[cityValidator]}>
        <AddressCityWithPermissions
          onBlur={onFieldBlur('nonUSCity')}
          onChange={onFieldChange('nonUSCity')}
          value={address.nonUSCity}
          className="address-input"
          dataTestId={getFieldName("nonUSCity")}
          shouldMask
        />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('postalCode')]}
        label="Postal Code"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[postCodeValidator]}>
        <AddressZipCodeWithPermissions
          onBlur={onFieldBlur('postalCode')}
          onChange={onFieldChange('postalCode')}
          value={address.postalCode}
          className="address-input"
          dataTestId={getFieldName("postalCode")}
          shouldMask
        />
      </ProfileLabeledInput>

      <ProfileLabeledInput
        errorMessage={invalidFields[getFieldName('country')]}
        label="Country"
        mailingType={mailingType}
        hasResidential={hasResidential}
        validators={[countryValidator]}>
        <AddressCountryWithPermissions
          value={address.country}
          onBlur={onFieldBlur('country')}
          onChange={onFieldChange('country')}
          options={countries}
          dataTestId={getFieldName("country")}
          shouldMask
        />
      </ProfileLabeledInput>
    </Fragment>
  );
};

NonUSAddress.propTypes = {
  address: object.isRequired,
  countries: array.isRequired,
  onChange: func.isRequired,
  invalidFields: object.isRequired,
  touchedFields: object.isRequired,
  mailingType: string
};

const mapStateToProps = (state) => ({
  countries: getCountries(state),
  invalidFields: state.invalidFields,
  touchedFields: state.touchedFields,
  hasResidential: hasResidentialAddress(state)
});

export default connect(mapStateToProps)(NonUSAddress);
